import { Controller } from "@hotwired/stimulus"

export default class AdvisorChatController extends Controller {
  static targets = ["messages", "input", "submit", "welcome", "turnstileContainer"]
  static values = { chatId: Number, verified: Boolean }

  declare messagesTarget: HTMLElement
  declare inputTarget: HTMLTextAreaElement
  declare submitTarget: HTMLButtonElement
  declare hasWelcomeTarget: boolean
  declare welcomeTarget: HTMLElement
  declare hasTurnstileContainerTarget: boolean
  declare turnstileContainerTarget: HTMLElement
  declare chatIdValue: number
  declare verifiedValue: boolean

  private observer: MutationObserver | null = null
  private waiting = false
  private turnstileVerified = false
  private activated = false
  private boundResize = () => this.sizeChatToViewport()

  connect() {
    this.turnstileVerified = this.verifiedValue
    this.observer = new MutationObserver(() => this.scrollToBottom())
    this.observer.observe(this.messagesTarget, { childList: true, subtree: true, characterData: true })

    if (this.element.classList.contains("advisor-chat--active")) {
      this.activated = true
      this.sizeChatToViewport()
      window.addEventListener("resize", this.boundResize)
      this.scrollNavToTop(true)
    }

    this.scrollToBottom()
  }

  disconnect() {
    this.observer?.disconnect()
    window.removeEventListener("resize", this.boundResize)
  }

  send(event: Event) {
    event.preventDefault()
    const message = this.inputTarget.value.trim()
    if (!message || this.waiting) return

    const turnstileToken = this.getTurnstileToken()
    if (!this.turnstileVerified && !turnstileToken) {
      alert("Please complete the security verification before sending.")
      return
    }

    this.hideWelcome()
    if (!this.activated) this.activateChat()
    this.appendUserMessage(message)
    this.postMessage(message, turnstileToken)
    this.inputTarget.value = ""
    this.autoResize()
    this.setWaiting(true)
  }

  sendSuggestion(event: Event) {
    const button = event.currentTarget as HTMLButtonElement
    const suggestion = button.dataset.suggestion
    if (!suggestion || this.waiting) return

    const turnstileToken = this.getTurnstileToken()
    if (!this.turnstileVerified && !turnstileToken) {
      alert("Please complete the security verification before sending.")
      return
    }

    this.hideWelcome()
    if (!this.activated) this.activateChat()
    this.appendUserMessage(suggestion)
    this.postMessage(suggestion, turnstileToken)
    this.setWaiting(true)
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.submitTarget.click()
    }
  }

  autoResize() {
    const el = this.inputTarget
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 160) + "px"
  }

  private activateChat() {
    this.activated = true
    this.element.classList.add("advisor-chat--active")
    this.sizeChatToViewport()
    window.addEventListener("resize", this.boundResize)
    this.scrollNavToTop(true)
    this.showNewChatButton()
  }

  private showNewChatButton() {
    const page = this.element.closest("[data-advisor-page]")
    const btn = page?.querySelector("[data-advisor-new-chat]") as HTMLElement | null
    if (btn) btn.hidden = false
  }

  private sizeChatToViewport() {
    const page = this.element.closest("[data-advisor-page]") as HTMLElement | null
    const nav = page?.querySelector(".arena-nav") as HTMLElement | null
    if (!page || !nav) return

    // Distance from nav top to chat top (nav + action bar + gaps)
    const above = this.element.offsetTop - nav.offsetTop
    this.element.style.height = `calc(100dvh - ${above + 5}px)`
  }

  private scrollNavToTop(animate = false) {
    const page = this.element.closest("[data-advisor-page]")
    const nav = page?.querySelector(".arena-nav")
    nav?.scrollIntoView({ block: "start", behavior: animate ? "smooth" : "instant" })
  }

  private hideWelcome() {
    if (this.hasWelcomeTarget) {
      this.welcomeTarget.remove()
    }
  }

  private appendUserMessage(content: string) {
    const html = `
      <div class="advisor-msg advisor-msg--user">
        <div class="advisor-msg__bubble">
          <div class="advisor-msg__content">${this.escapeHtml(content)}</div>
        </div>
      </div>
    `
    // Insert before the streaming target
    const streaming = this.messagesTarget.querySelector("#advisor_streaming")
    if (streaming) {
      streaming.insertAdjacentHTML("beforebegin", html)
    } else {
      this.messagesTarget.insertAdjacentHTML("beforeend", html)
    }

    this.showTypingIndicator()
  }

  private showTypingIndicator() {
    const streaming = this.messagesTarget.querySelector("#advisor_streaming")
    if (streaming) {
      streaming.innerHTML = `
        <div class="advisor-msg advisor-msg--assistant">
          <div class="advisor-msg__avatar">
            <svg class="size-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
          </div>
          <div class="advisor-msg__bubble">
            <div class="advisor-msg__content advisor-msg__typing">Thinking…</div>
          </div>
        </div>
      `
    }
  }

  private async postMessage(message: string, turnstileToken: string | null) {
    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
    const body: Record<string, string> = { message }
    if (turnstileToken) {
      body["cf-turnstile-response"] = turnstileToken
    }

    try {
      const response = await fetch("/arena/model-advisor/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token || "",
          Accept: "text/vnd.turbo-stream.html",
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        this.showError("Failed to send message. Please try again.")
        this.setWaiting(false)
      } else {
        try { window.umami?.track("model-advisor-message") } catch {}
        if (!this.turnstileVerified) {
          // First successful send — server set the cookie, hide the widget
          this.turnstileVerified = true
          this.hideTurnstile()
        }
      }
      // Response comes via Turbo Stream broadcast — the streaming target will be updated
    } catch {
      this.showError("Connection error. Please check your network and try again.")
      this.setWaiting(false)
    }
  }

  private showError(text: string) {
    const streaming = this.messagesTarget.querySelector("#advisor_streaming")
    if (streaming) {
      streaming.innerHTML = `
        <div class="advisor-msg advisor-msg--assistant">
          <div class="advisor-msg__avatar advisor-msg__avatar--error">
            <svg class="size-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <div class="advisor-msg__bubble advisor-msg__bubble--error">
            <div class="advisor-msg__content">${this.escapeHtml(text)}</div>
          </div>
        </div>
      `
    }
  }

  private setWaiting(val: boolean) {
    this.waiting = val
    this.submitTarget.disabled = val
    this.inputTarget.disabled = val
    if (!val) {
      this.inputTarget.focus()
    }
  }

  // Re-enable input when streaming target gets replaced with a complete message.
  // Move the finished message out of #advisor_streaming so the next turn's
  // typing indicator doesn't overwrite it.
  private scrollToBottom() {
    const streaming = this.messagesTarget.querySelector("#advisor_streaming")
    if (streaming) {
      // The complete partial wraps the message in a plain #advisor_streaming div,
      // while the streaming partial puts .advisor-msg directly on #advisor_streaming.
      const isComplete =
        !streaming.classList.contains("advisor-msg") &&
        streaming.querySelector(".advisor-msg__content.prose")
      const hasError = streaming.querySelector(".advisor-msg__bubble--error")
      if (isComplete || hasError) {
        while (streaming.firstChild) {
          streaming.parentElement!.insertBefore(streaming.firstChild, streaming)
        }
        this.setWaiting(false)
      }
    }

    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    })
  }

  private getTurnstileToken(): string | null {
    if (!this.hasTurnstileContainerTarget) return null

    const responseInput = this.turnstileContainerTarget.querySelector<HTMLInputElement>(
      'input[name="cf-turnstile-response"]'
    )

    return responseInput?.value || null
  }

  private hideTurnstile(): void {
    if (!this.hasTurnstileContainerTarget) return
    this.turnstileContainerTarget.hidden = true
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
